import * as THREE from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EOpenPoseCursorAxis } from '../enums/e_open_pose_cursor_axis';
import { EOpenPoseCursorPlane } from '../enums/e_open_pose_cursor_plane';

const {
  AXIS_CURSOR_NAME,
  AXIS_CURSOR_Z_AXIS_COLOR,
  AXIS_CURSOR_AXIS_LENGTH_WORLD,
  AXIS_CURSOR_CONE_RADIUS_WORLD,
  AXIS_CURSOR_CYLINDER_RADIUS_WORLD,
  AXIS_CURSOR_CONE_HEIGHT_WORLD,
  AXIS_CURSOR_X_AXIS_COLOR,
  AXIS_CURSOR_Y_AXIS_COLOR,
  AXIS_CURSOR_XY_PLANE_COLOR,
  AXIS_CURSOR_PLANE_SIZE_WORLD,
  AXIS_CURSOR_XZ_PLANE_COLOR,
  AXIS_CURSOR_YZ_PLANE_COLOR,
  AXIS_CURSOR_EPSILON_SMALL,
  AXIS_CURSOR_CYLINDER_POSTFIX,
  CURSOR_AXIS_RENDER_ORDER,
  AXIS_CURSOR_CONE_POSTFIX_1,
  AXIS_CURSOR_CONE_POSTFIX_2,
  CURSOR_AXIS_PLANE_RENDER_ORDER,
  CURSOR_PART_CYLINDER,
  CURSOR_PART_CONE,
  AXIS_CURSOR_DIAMETER_MULTIPLIER,
  AXIS_CURSOR_PLANE_SCALE_MULTIPLIER,
} = ConstantsOpenPose;

export class OpenPoseCursorAxis extends THREE.Group {
  /*
   * FIELDS
   */
  private map_cylinder_by_axis: Record<EOpenPoseCursorAxis, THREE.Mesh>;
  private map_cones_by_axis: Record<EOpenPoseCursorAxis, [THREE.Mesh, THREE.Mesh]>;
  private map_plane_mesh_by_plane: Record<EOpenPoseCursorPlane, THREE.Mesh>;
  private map_original_scale_by_object: Map<THREE.Object3D<THREE.Object3DEventMap>, THREE.Vector3>;

  /*
   * METHODS
   */
  constructor() {
    super();

    this.map_cylinder_by_axis = {} as any;
    this.map_cones_by_axis = {} as any;
    this.map_plane_mesh_by_plane = {} as any;

    this.map_original_scale_by_object = new Map<THREE.Object3D, THREE.Vector3>();

    this.name = AXIS_CURSOR_NAME;
    this.create_cursor_geometry_and_materials();
  }

  private create_cursor_geometry_and_materials() {
    const axis_length_world = AXIS_CURSOR_AXIS_LENGTH_WORLD;
    const cylinder_radius_world = AXIS_CURSOR_CYLINDER_RADIUS_WORLD;
    const cone_radius_world = AXIS_CURSOR_CONE_RADIUS_WORLD;
    const cone_height_world = AXIS_CURSOR_CONE_HEIGHT_WORLD;

    this.create_single_axis(
      EOpenPoseCursorAxis.X,
      AXIS_CURSOR_X_AXIS_COLOR,
      new THREE.Vector3(1, 0, 0),
      axis_length_world,
      cylinder_radius_world,
      cone_radius_world,
      cone_height_world,
    );

    this.create_single_axis(
      EOpenPoseCursorAxis.Y,
      AXIS_CURSOR_Y_AXIS_COLOR,
      new THREE.Vector3(0, 1, 0),
      axis_length_world,
      cylinder_radius_world,
      cone_radius_world,
      cone_height_world,
    );

    this.create_single_axis(
      EOpenPoseCursorAxis.Z,
      AXIS_CURSOR_Z_AXIS_COLOR,
      new THREE.Vector3(0, 0, 1),
      axis_length_world,
      cylinder_radius_world,
      cone_radius_world,
      cone_height_world,
    );

    this.create_single_plane(
      EOpenPoseCursorPlane.XY,
      AXIS_CURSOR_XY_PLANE_COLOR,
      new THREE.Vector3(3, 3, 0),
      new THREE.Vector3(0, 0, 0),
      AXIS_CURSOR_PLANE_SIZE_WORLD,
    );
    this.create_single_plane(
      EOpenPoseCursorPlane.XZ,
      AXIS_CURSOR_XZ_PLANE_COLOR,
      new THREE.Vector3(3, 0, 3),
      new THREE.Vector3(-Math.PI / 2, 0, 0),
      AXIS_CURSOR_PLANE_SIZE_WORLD,
    );
    this.create_single_plane(
      EOpenPoseCursorPlane.YZ,
      AXIS_CURSOR_YZ_PLANE_COLOR,
      new THREE.Vector3(0, 3, 3),
      new THREE.Vector3(0, Math.PI / 2, 0),
      AXIS_CURSOR_PLANE_SIZE_WORLD,
    );
  }

  private create_single_axis(
    axis_name: EOpenPoseCursorAxis,
    axis_color: number,
    axis_direction_unit: THREE.Vector3,
    axis_length_world: number,
    cylinder_radius_world: number,
    cone_radius_world: number,
    cone_height_world: number,
  ) {
    const epsilon_small = AXIS_CURSOR_EPSILON_SMALL;
    const shaft_length_world = axis_length_world - 2 * epsilon_small;

    const shaft_geometry = new THREE.CylinderGeometry(cylinder_radius_world, cylinder_radius_world, shaft_length_world, 16);
    const shaft_material = new THREE.MeshBasicMaterial({
      color: axis_color,
      transparent: true,
      opacity: 0.8,
      depthTest: false,
      depthWrite: false,
    });

    const shaft_mesh = new THREE.Mesh(shaft_geometry, shaft_material);
    shaft_mesh.name = `${axis_name}${AXIS_CURSOR_CYLINDER_POSTFIX}`;
    shaft_mesh.renderOrder = CURSOR_AXIS_RENDER_ORDER;

    if (axis_direction_unit.x === 1) shaft_mesh.rotation.z = -Math.PI / 2;
    else if (axis_direction_unit.z === 1) shaft_mesh.rotation.x = Math.PI / 2;

    this.add(shaft_mesh);
    this.map_cylinder_by_axis[axis_name] = shaft_mesh;
    this.map_original_scale_by_object.set(shaft_mesh, shaft_mesh.scale.clone());

    const cone_geometry = new THREE.ConeGeometry(cone_radius_world, cone_height_world, 16);
    const cone_material = new THREE.MeshBasicMaterial({
      color: axis_color,
      transparent: true,
      opacity: 0.8,
      depthTest: false,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const cone_mesh_positive = new THREE.Mesh(cone_geometry, cone_material.clone());
    const cone_mesh_negative = new THREE.Mesh(cone_geometry, cone_material.clone());

    cone_mesh_positive.name = `${axis_name}${AXIS_CURSOR_CONE_POSTFIX_1}`;
    cone_mesh_negative.name = `${axis_name}${AXIS_CURSOR_CONE_POSTFIX_2}`;

    cone_mesh_positive.renderOrder = CURSOR_AXIS_RENDER_ORDER;
    cone_mesh_negative.renderOrder = CURSOR_AXIS_RENDER_ORDER;

    const half_shaft_length = shaft_length_world / 2;
    const center_position_world = half_shaft_length + cone_height_world / 2;

    if (axis_direction_unit.x === 1) {
      cone_mesh_positive.position.set(+center_position_world, 0, 0);
      cone_mesh_negative.position.set(-center_position_world, 0, 0);
      cone_mesh_positive.rotation.z = -Math.PI / 2;
      cone_mesh_negative.rotation.z = Math.PI / 2;
    } else if (axis_direction_unit.y === 1) {
      cone_mesh_positive.position.set(0, +center_position_world, 0);
      cone_mesh_negative.position.set(0, -center_position_world, 0);
      cone_mesh_negative.rotation.x = Math.PI;
    } else if (axis_direction_unit.z === 1) {
      cone_mesh_positive.position.set(0, 0, +center_position_world);
      cone_mesh_negative.position.set(0, 0, -center_position_world);
      cone_mesh_positive.rotation.x = Math.PI / 2;
      cone_mesh_negative.rotation.x = -Math.PI / 2;
    }

    this.add(cone_mesh_positive, cone_mesh_negative);
    this.map_cones_by_axis[axis_name] = [cone_mesh_positive, cone_mesh_negative];

    this.map_original_scale_by_object.set(cone_mesh_positive, cone_mesh_positive.scale.clone());
    this.map_original_scale_by_object.set(cone_mesh_negative, cone_mesh_negative.scale.clone());
  }

  private create_single_plane(
    plane_name: EOpenPoseCursorPlane,
    plane_color: number,
    plane_local_position: THREE.Vector3,
    plane_local_rotation_euler: THREE.Vector3,
    plane_size_world: number,
  ) {
    const plane_geometry = new THREE.PlaneGeometry(plane_size_world, plane_size_world);
    const plane_material = new THREE.MeshBasicMaterial({
      color: plane_color,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    const plane_mesh = new THREE.Mesh(plane_geometry, plane_material);

    plane_mesh.position.copy(plane_local_position);
    plane_mesh.rotation.set(plane_local_rotation_euler.x, plane_local_rotation_euler.y, plane_local_rotation_euler.z);
    plane_mesh.name = plane_name;
    plane_mesh.renderOrder = CURSOR_AXIS_PLANE_RENDER_ORDER;

    this.add(plane_mesh);

    this.map_plane_mesh_by_plane[plane_name] = plane_mesh;
    this.map_original_scale_by_object.set(plane_mesh, plane_mesh.scale.clone());
  }

  public highlight_axis(axis_name: EOpenPoseCursorAxis) {
    for (const child of this.children) {
      if (child instanceof THREE.Mesh) {
        child.scale.set(1, 1, 1);
        const material = child.material as THREE.MeshBasicMaterial;
        material.opacity = 1;
        material.transparent = true;
      }
    }

    if (!axis_name) return;

    for (const child of this.children) {
      if (!(child instanceof THREE.Mesh)) continue;
      const material = child.material as THREE.MeshBasicMaterial;

      if (child.name.startsWith(axis_name) && (child.name.includes(CURSOR_PART_CYLINDER) || child.name.includes(CURSOR_PART_CONE))) {
        child.scale.set(child.scale.x * 2, child.scale.y, child.scale.z * 2);
        material.opacity = 1;
      } else {
        material.opacity = 0.3;
        material.transparent = true;
      }
      material.needsUpdate = true;
    }
  }

  public highlight_plane(plane_name: EOpenPoseCursorPlane) {
    for (const [plane_key, plane_mesh] of Object.entries(this.map_plane_mesh_by_plane) as [EOpenPoseCursorPlane, THREE.Mesh][]) {
      const is_active_plane = plane_name === plane_key;
      const material = plane_mesh.material as THREE.MeshBasicMaterial;

      material.opacity = is_active_plane ? 1.0 : plane_name ? 0.0 : 0.6;
      plane_mesh.scale.copy(this.map_original_scale_by_object.get(plane_mesh)!);

      if (is_active_plane) plane_mesh.scale.multiplyScalar(AXIS_CURSOR_PLANE_SCALE_MULTIPLIER);

      material.needsUpdate = true;
    }

    const axis_to_hide_by_plane: Partial<Record<EOpenPoseCursorPlane, EOpenPoseCursorAxis>> = {
      XY_PLANE: EOpenPoseCursorAxis.Z,
      XZ_PLANE: EOpenPoseCursorAxis.Y,
      YZ_PLANE: EOpenPoseCursorAxis.X,
    };

    const axes_in_plane: Partial<Record<EOpenPoseCursorPlane, EOpenPoseCursorAxis[]>> = {
      XY_PLANE: [EOpenPoseCursorAxis.X, EOpenPoseCursorAxis.Y],
      XZ_PLANE: [EOpenPoseCursorAxis.X, EOpenPoseCursorAxis.Z],
      YZ_PLANE: [EOpenPoseCursorAxis.Y, EOpenPoseCursorAxis.Z],
    };

    for (const axis_loop of [EOpenPoseCursorAxis.X, EOpenPoseCursorAxis.Y, EOpenPoseCursorAxis.Z]) {
      const should_hide_this_axis = plane_name ? axis_to_hide_by_plane[plane_name] === axis_loop : false;
      const cylinder_mesh = this.map_cylinder_by_axis[axis_loop];
      const [cone_mesh_positive, cone_mesh_negative] = this.map_cones_by_axis[axis_loop];

      const target_opacity = should_hide_this_axis ? 0.0 : 0.8;

      (cylinder_mesh.material as THREE.MeshBasicMaterial).opacity = target_opacity;
      cylinder_mesh.scale.copy(this.map_original_scale_by_object.get(cylinder_mesh)!);
      (cylinder_mesh.material as THREE.MeshBasicMaterial).needsUpdate = true;

      for (const cone_mesh of [cone_mesh_positive, cone_mesh_negative]) {
        (cone_mesh.material as THREE.MeshBasicMaterial).opacity = target_opacity;
        cone_mesh.scale.copy(this.map_original_scale_by_object.get(cone_mesh)!);
        (cone_mesh.material as THREE.MeshBasicMaterial).needsUpdate = true;
      }

      if (plane_name && axes_in_plane[plane_name]?.includes(axis_loop)) {
        const scale_diameter_multiplier = AXIS_CURSOR_DIAMETER_MULTIPLIER;
        cylinder_mesh.scale.set(cylinder_mesh.scale.x * scale_diameter_multiplier, cylinder_mesh.scale.y, cylinder_mesh.scale.z * scale_diameter_multiplier);
        cone_mesh_positive.scale.set(
          cone_mesh_positive.scale.x * scale_diameter_multiplier,
          cone_mesh_positive.scale.y,
          cone_mesh_positive.scale.z * scale_diameter_multiplier,
        );
        cone_mesh_negative.scale.set(
          cone_mesh_negative.scale.x * scale_diameter_multiplier,
          cone_mesh_negative.scale.y,
          cone_mesh_negative.scale.z * scale_diameter_multiplier,
        );
      }
    }
  }

  public update_children_visibility(part_name: string, is_visible: boolean) {
    for (const child of this.children) {
      if (child instanceof THREE.Mesh && child.name.includes(part_name)) child.visible = is_visible;
    }
  }

  private dispose_material_safe(material: THREE.Material | THREE.Material[]) {
    if (Array.isArray(material)) for (const single_material of material) single_material.dispose();
    else material.dispose();
  }

  public dispose() {
    for (const cylinder_mesh of Object.values(this.map_cylinder_by_axis)) {
      cylinder_mesh.geometry.dispose();
      this.dispose_material_safe(cylinder_mesh.material);
    }
    for (const [cone_mesh_positive, cone_mesh_negative] of Object.values(this.map_cones_by_axis)) {
      for (const cone_mesh of [cone_mesh_positive, cone_mesh_negative]) {
        cone_mesh.geometry.dispose();
        this.dispose_material_safe(cone_mesh.material);
      }
    }
    for (const plane_mesh of Object.values(this.map_plane_mesh_by_plane)) {
      plane_mesh.geometry.dispose();
      this.dispose_material_safe(plane_mesh.material);
    }
  }
}
